import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useDeleteGeoSchemeMutation,
  useGetGeoSchemeQuery,
  useUpdateGeoSchemeMutation,
} from '@shared/api';
import {
  apiErrorMessage,
  fmtDateTime,
  workTypeShort,
  workTypeTitle,
  type GeoScheme,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { IconArrowLeft, IconPencil } from '@shared/ui/icons';
import {
  ErrorText,
  Eyebrow,
  IconButton,
  LinkButton,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  RequestBriefLink,
  Row,
  SchemeActions,
  SchemeJournal,
  SchemeStatusBadge,
  Stack,
  useAction,
  useGeoObjectNames,
} from '@widgets/GeoSchemes';
import {
  computeStepStatuses,
  defaultStep,
  STEPS,
  Stepper,
  WizardProvider,
  type WizardContextValue,
} from './wizard';
import { SheetRdUpdateNotice } from './RdSheet';
import { StepSheet } from './StepSheet';
import { StepCrs } from './StepCrs';
import { StepScale } from './StepScale';
import { StepDraft } from './StepDraft';
import { StepLayout } from './StepLayout';
import { StepData } from './StepData';
import { StepDeviations } from './StepDeviations';
import { StepDrawing } from './StepDrawing';
import { StepCorrections } from './StepCorrections';

const Workspace = styled.div`
  container-type: inline-size;
  container-name: geo-wizard;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  width: max-content;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Head = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(6)}`};
  align-items: start;
  padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(6)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  @container geo-wizard (max-width: 860px) {
    grid-template-columns: 1fr;
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

const SchemeNo = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  letter-spacing: 0;
  text-transform: none;
`;

const Title = styled.h2`
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 ${theme.spacing(2)}`};
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.25;
  text-wrap: balance;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const TitleForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: ${({ theme }) => `${theme.spacing(1.5)} 0 ${theme.spacing(2)}`};

  input {
    flex: 1 1 320px;
    font-size: 16px;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};

  @container geo-wizard (max-width: 860px) {
    align-items: flex-start;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(6)};
  align-items: start;

  @container geo-wizard (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(4)};
  }
`;

const Rail = styled.div`
  position: sticky;
  top: 0;
  max-height: calc(100vh - 120px);
  overflow: auto;

  @container geo-wizard (max-width: 860px) {
    position: static;
    max-height: none;
  }
`;

const IN_REVIEW = new Set(['submitted', 'accepted', 'sent_to_customer']);

function TitleEditor({ scheme }: { scheme: GeoScheme }) {
  const [update] = useUpdateGeoSchemeMutation();
  const { run, busy, error } = useAction();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(scheme.title);

  if (!scheme.permissions.can_edit || !editing) {
    return (
      <TitleRow>
        <Title>{scheme.title}</Title>
        {scheme.permissions.can_edit && (
          <IconButton
            type="button"
            aria-label="Переименовать схему"
            onClick={() => {
              setValue(scheme.title);
              setEditing(true);
            }}
          >
            <IconPencil size={16} />
          </IconButton>
        )}
      </TitleRow>
    );
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const title = value.trim();
    if (!title || title === scheme.title) {
      setEditing(false);
      return;
    }
    const ok = await run(
      () => update({ id: scheme.id, title }).unwrap(),
      'Наименование не сохранено.',
    );
    if (ok) setEditing(false);
  }

  return (
    <>
      <TitleForm onSubmit={(e) => void submit(e)}>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label="Наименование схемы"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <Button type="submit" $size="sm" disabled={busy || !value.trim()}>
          {busy ? 'Сохранение…' : 'Сохранить'}
        </Button>
        <Button type="button" $size="sm" $variant="ghost" onClick={() => setEditing(false)}>
          Отмена
        </Button>
      </TitleForm>
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </>
  );
}

/**
 * Карточка ИС `/agents/geodesy/schemes/:id` — мастер из 9 шагов регламента
 * камеральщика (ТЗ §1.1): степпер слева, рабочая область справа, журнал ниже.
 * Шаг — в `?step=`, чтобы ссылка и «назад» вели туда же.
 */
export function GeoSchemeCard() {
  const { id = '' } = useParams<{ id: string }>();
  const {
    data: scheme,
    isLoading,
    error,
  } = useGetGeoSchemeQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: 30,
  });

  if (isLoading) return <Muted>Загрузка схемы…</Muted>;
  if (error || !scheme) {
    return (
      <Stack $gap={3}>
        <Back to="/agents/geodesy">
          <IconArrowLeft size={16} /> Реестр схем
        </Back>
        <ErrorText role="alert">
          {apiErrorMessage(error) ?? 'Схема не найдена или нет доступа.'}
        </ErrorText>
      </Stack>
    );
  }
  return <SchemeWorkspace key={scheme.id} scheme={scheme} />;
}

function SchemeWorkspace({ scheme }: { scheme: GeoScheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const names = useGeoObjectNames();
  const [remove] = useDeleteGeoSchemeMutation();
  const removing = useAction();
  const topRef = useRef<HTMLDivElement>(null);

  const readOnly = !scheme.permissions.can_edit;
  const [initialStep] = useState(() => defaultStep(scheme));
  const stepParam = Number(params.get('step'));
  const step = stepParam >= 1 && stepParam <= STEPS.length ? stepParam : initialStep;

  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});
  const anyDirty = Object.values(dirtyMap).some(Boolean);
  const setDirty = useCallback((key: string, dirty: boolean) => {
    setDirtyMap((m) => (Boolean(m[key]) === dirty ? m : { ...m, [key]: dirty }));
  }, []);

  // Закрытие вкладки с несохранёнными правками — штатное предупреждение браузера.
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  const goTo = useCallback(
    (n: number, anchor?: string, force = false) => {
      if (!force && n !== step && anyDirty) {
        const leave = window.confirm(
          'На этом шаге есть несохранённые изменения. Перейти без сохранения?',
        );
        if (!leave) return;
      }
      const sp = new URLSearchParams(location.search);
      sp.set('step', String(n));
      navigate({ search: `?${sp.toString()}`, hash: anchor ? `#${anchor}` : '' });
      if (!anchor) topRef.current?.scrollIntoView({ block: 'start' });
    },
    [step, anyDirty, location.search, navigate],
  );

  const ctx = useMemo<WizardContextValue>(
    () => ({ scheme, readOnly, step, goTo, setDirty }),
    [scheme, readOnly, step, goTo, setDirty],
  );
  const statuses = computeStepStatuses(scheme);

  async function deleteDraft() {
    if (!window.confirm(`Удалить черновик схемы ${scheme.number}? Это действие не отменить.`))
      return;
    const ok = await removing.run(() => remove(scheme.id).unwrap(), 'Черновик не удалён.');
    if (ok) navigate('/agents/geodesy');
  }

  const objectName = scheme.object_name || names.project(scheme.project_id);
  const site = names.site(scheme.site_id);

  return (
    <WizardProvider value={ctx}>
      <Workspace ref={topRef}>
        <Back to="/agents/geodesy">
          <IconArrowLeft size={16} /> Реестр схем
        </Back>

        <Head>
          <div>
            <Eyebrow>
              Исполнительная схема · <SchemeNo>{scheme.number}</SchemeNo>
            </Eyebrow>
            <TitleEditor scheme={scheme} />
            <Meta>
              <SchemeStatusBadge status={scheme.status} rejectedBy={scheme.rejected_by} />
              <span>
                {workTypeShort(scheme.work_type)} — {workTypeTitle(scheme.work_type).toLowerCase()}
              </span>
              {objectName && <span>{objectName}</span>}
              {site && <span>площадка: {site}</span>}
              <span>
                {scheme.current_version > 0 ? `версия ${scheme.current_version}` : 'версий нет'}
              </span>
              <span>обновлена {fmtDateTime(scheme.updated_at)}</span>
            </Meta>
            {scheme.request && (
              <Row $gap={2}>
                <RequestBriefLink request={scheme.request} />
              </Row>
            )}
          </div>
          <Actions>
            <SchemeActions scheme={scheme} />
            {scheme.status === 'draft' && scheme.permissions.can_edit && (
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => void deleteDraft()}
                disabled={removing.busy}
              >
                Удалить черновик
              </Button>
            )}
            {removing.error && <ErrorText role="alert">{removing.error}</ErrorText>}
          </Actions>
        </Head>

        {scheme.status === 'rejected' && (
          <Notice $tone="danger" role="alert">
            <strong>
              {scheme.rejected_by === 'customer' ? 'Отклонена заказчиком' : 'Отклонена ПТО'} —
              требует корректировки
            </strong>
            <span>{scheme.reject_reason ?? 'Причина — в журнале схемы.'}</span>
            {step !== 9 && (
              <div>
                <LinkButton type="button" onClick={() => goTo(9)}>
                  Перейти к корректировкам →
                </LinkButton>
              </div>
            )}
          </Notice>
        )}
        {IN_REVIEW.has(scheme.status) && (
          <Notice $tone="info" role="status">
            {scheme.status === 'submitted'
              ? 'Схема на проверке в ОСНОВА.Инженер. Решение и замечания появятся в журнале.'
              : scheme.status === 'accepted'
                ? 'Схема принята ПТО. Следующий шаг — передача заказчику.'
                : 'Схема у заказчика на согласовании.'}
          </Notice>
        )}
        <SheetRdUpdateNotice scheme={scheme} />
        {scheme.status === 'approved' && (
          <Notice $tone="success" role="status">
            Схема согласована заказчиком — можно подшивать в исполнительную документацию.
          </Notice>
        )}

        <Grid>
          <Rail>
            <Stepper current={step} statuses={statuses} onSelect={(n) => goTo(n)} />
          </Rail>
          <div>
            {step === 1 && <StepSheet />}
            {step === 2 && <StepCrs />}
            {step === 3 && <StepScale />}
            {step === 4 && <StepDraft />}
            {step === 5 && <StepLayout />}
            {step === 6 && <StepData />}
            {step === 7 && <StepDeviations />}
            {step === 8 && <StepDrawing />}
            {step === 9 && <StepCorrections />}
          </div>
        </Grid>

        <Panel aria-labelledby="geo-journal-title">
          <PanelHead>
            <PanelTitle id="geo-journal-title">Журнал и обсуждение</PanelTitle>
            <Muted>переходы, причины отклонения, комментарии ПТО и геодезиста</Muted>
          </PanelHead>
          <SchemeJournal
            scheme={scheme}
            canComment={scheme.permissions.can_edit || scheme.permissions.can_review}
          />
        </Panel>
      </Workspace>
    </WizardProvider>
  );
}
